import {NgStyle} from '@angular/common';
import {ChangeDetectionStrategy, Component, computed, input, output} from '@angular/core';
import {MatMenuModule} from '@angular/material/menu';

export type ScheduleChipType = 'multiple' | 'morning' | 'afternoon' | 'evening' | 'night' | null;

const CHIP_TYPE_OPTIONS: ScheduleChipType[] = [null, 'morning', 'afternoon', 'evening', 'night', 'multiple'];

interface ChipConfig {
  backgroundColor: string;
  borderColor: string;
  color: string;
  icon: string | null;
  label: string;
}

const CHIP_CONFIGS: Record<string, ChipConfig> = {
  multiple: {
    backgroundColor: '#fefefe',
    borderColor: '#e5e3dc',
    color: '#4a443d',
    icon: 'assets/img/icons/challenge-builder/multiple.svg',
    label: 'Multiple',
  },
  morning: {
    backgroundColor: '#fff8cd',
    borderColor: '#fbb977',
    color: '#b34703',
    icon: 'assets/img/icons/challenge-builder/morning.svg',
    label: 'Morning',
  },
  afternoon: {
    backgroundColor: '#dbffe2',
    borderColor: '#4ed976',
    color: '#0e742b',
    icon: 'assets/img/icons/challenge-builder/afternoon.svg',
    label: 'Afternoon',
  },
  evening: {
    backgroundColor: '#e1fcff',
    borderColor: '#81c3ea',
    color: '#0c4f76',
    icon: 'assets/img/icons/challenge-builder/evening.svg',
    label: 'Evening',
  },
  night: {
    backgroundColor: '#ebebff',
    borderColor: '#c398e0',
    color: '#48269c',
    icon: 'assets/img/icons/challenge-builder/night.svg',
    label: 'Night',
  },
};

const DEFAULT_CONFIG: ChipConfig = {
  backgroundColor: '#ededed',
  borderColor: '#e5e3dc',
  color: '#4a443d',
  icon: null,
  label: 'No Activity',
};

@Component({
  selector: 'cb-schedule-chip',
  standalone: true,
  imports: [NgStyle, MatMenuModule],
  template: `
    <button
      class="chip"
      [class.icon-only]="iconOnly()"
      [class.has-menu]="menu()"
      [class.hide-border]="hideBorder()"
      [ngStyle]="{
        'background-color': config().backgroundColor,
        'border-color': config().borderColor,
        color: config().color
      }"
      [matMenuTriggerFor]="menu() ? checkInMenu : null"
    >
      @if (config().icon) {
        <img class="chip-icon" [src]="config().icon" [alt]="config().label + ' icon'" />
      }
      @if (!iconOnly()) {
        <span class="chip-label">{{ config().label }}</span>
      }
      @if (iconOnly() && !config().icon) {
        <span class="select-label">select</span>
      }
    </button>

    <mat-menu #checkInMenu="matMenu" class="check-in-menu" panelClass="check-in-menu-panel">
      <ng-template matMenuContent>
        @for (option of chipOptions; track option) {
          <button mat-menu-item class="check-in-option" (click)="onOptionSelect(option)">
            <div
              class="menu-chip"
              [ngStyle]="{
                'background-color': getConfigForType(option).backgroundColor,
                'border-color': getConfigForType(option).borderColor,
                color: getConfigForType(option).color
              }"
            >
              @if (getConfigForType(option).icon) {
                <img
                  class="chip-icon"
                  [src]="getConfigForType(option).icon"
                  [alt]="getConfigForType(option).label + ' icon'"
                />
              }
              <span class="chip-label">{{ getConfigForType(option).label }}</span>
            </div>
          </button>
        }
      </ng-template>
    </mat-menu>
  `,
  styles: [
    `
      :host {
        display: inline-block;
      }

      .chip {
        display: flex;
        align-items: center;
        gap: 10px;
        height: 36px;
        padding: 10px;
        border: 1px solid;
        border-radius: 8px;
        background: none;
        cursor: default;
        font-family: 'Asap', sans-serif;
        font-weight: 400;
        font-size: 14px;
        line-height: 16px;
        white-space: nowrap;
        width: 100%;
        text-align: left;
        pointer-events: none;

        &.has-menu {
          pointer-events: auto;
          cursor: pointer;
        }

        &.icon-only {
          justify-content: center;
          min-width: 0;
        }

        &.hide-border {
          border: none;
        }
      }

      .chip-icon {
        width: 18px;
        height: 18px;
        flex-shrink: 0;
        display: block;
      }

      .chip-label {
        flex: 1;
      }

      .select-label {
        color: #7f7f7f;
        text-align: center;
        font-family: 'Asap', sans-serif;
        font-size: 14px;
        font-weight: 400;
        line-height: 16px;
      }

      .menu-chip {
        display: flex;
        align-items: center;
        gap: 10px;
        height: 36px;
        padding: 10px;
        border: 1px solid;
        border-radius: 8px;
        font-family: 'Asap', sans-serif;
        font-weight: 400;
        font-size: 14px;
        line-height: 16px;
        white-space: nowrap;
        width: 100%;
        text-align: left;
      }

      ::ng-deep .mat-mdc-menu-panel.check-in-menu-panel {
        min-width: 166px;

        .mat-mdc-menu-content {
          display: flex;
          flex-direction: column;
          padding: 8px;
        }

        .mat-mdc-menu-item.check-in-option {
          width: 166px;
          padding: 0;
          height: auto;
          min-height: unset;

          .mat-mdc-menu-item-text {
            width: 100%;
          }

          .menu-chip {
            width: 100%;
          }
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CbScheduleChipComponent {
  type = input<ScheduleChipType>(null);
  iconOnly = input<boolean>(false);
  menu = input<boolean>(false);
  defaultBorderColor = input<boolean>(false);
  hideBorder = input<boolean>(false);

  selectType = output<ScheduleChipType>();

  chipOptions = CHIP_TYPE_OPTIONS;

  config = computed<ChipConfig>(() => {
    const chipType = this.type();
    const isIconOnly = this.iconOnly();
    const borderColor = this.defaultBorderColor() ? '#e5e3dc' : CHIP_CONFIGS[chipType]?.borderColor || '#e5e3dc';

    if (!chipType) {
      return {
        ...DEFAULT_CONFIG,
        label: isIconOnly ? 'select' : 'No Activity',
      };
    }

    return {
      ...(CHIP_CONFIGS[chipType] || DEFAULT_CONFIG),
      borderColor,
    };
  });

  getConfigForType(chipType: ScheduleChipType): ChipConfig {
    if (!chipType) {
      return DEFAULT_CONFIG;
    }
    return CHIP_CONFIGS[chipType] || DEFAULT_CONFIG;
  }

  onOptionSelect(option: ScheduleChipType): void {
    this.selectType.emit(option);
  }
}
